import React, { useMemo } from 'react';
import { Autocomplete, Box, Button, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { fmtDay } from '../../OnlineJournal/format';
import { TYPE_OPTIONS } from '../constants';
import { plural } from '../format';
import { DocumentsMeta, PharmacyOption } from '../types';
import { DEFAULT_ANALYTICS_FILTERS, PRESETS, ResolvedPeriod, hasAnalyticsFilters, localToday, prevPeriodOf } from './analyticsFormat';
import { AnalyticsFilters, Preset } from './analyticsTypes';

interface Props {
  meta: DocumentsMeta;
  filters: AnalyticsFilters;
  period: ResolvedPeriod;
  onChange: (patch: Partial<AnalyticsFilters>) => void;
}

const BORDER = '#e2e8f0';
const fieldSx = { '& .MuiInputBase-root': { bgcolor: '#fff' } };

/** Фильтры аналитики: период, тип, маршрут, маркировка, аптеки, регионы */
const AnalyticsToolbar: React.FC<Props> = ({ meta, filters, period, onChange }) => {
  const pharmacyByGuid = useMemo(() => new Map(meta.pharmacies.map(p => [p.guid, p])), [meta.pharmacies]);
  const selected = filters.pharmacy.map(g => pharmacyByGuid.get(g)).filter(Boolean) as PharmacyOption[];
  const prev = period.error ? null : prevPeriodOf(period);

  const pickPreset = (value: Preset | null) => {
    if (!value) return;
    // «Период» открывается с датами текущего пресета - их удобнее поправить, чем вводить заново
    if (value === 'custom' && filters.preset !== 'custom') onChange({ preset: value, from: period.from, to: period.to });
    else onChange({ preset: value });
  };

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: '#fff', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small" exclusive value={filters.preset} onChange={(_, v: Preset | null) => pickPreset(v)}
          sx={{ height: 36, '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, borderColor: BORDER } }}
        >
          {PRESETS.map(p => <ToggleButton key={p.value} value={p.value}>{p.label}</ToggleButton>)}
        </ToggleButtonGroup>
        {filters.preset === 'custom' && (
          <>
            <TextField
              size="small" type="date" label="С" value={filters.from} onChange={e => onChange({ from: e.target.value })}
              InputLabelProps={{ shrink: true }} inputProps={{ max: localToday() }} sx={{ width: 160, ...fieldSx }}
            />
            <TextField
              size="small" type="date" label="По" value={filters.to} onChange={e => onChange({ to: e.target.value })}
              InputLabelProps={{ shrink: true }} inputProps={{ max: localToday() }} sx={{ width: 160, ...fieldSx }}
            />
          </>
        )}
        <TextField
          select size="small" label="Тип" value={filters.type}
          onChange={e => onChange({ type: e.target.value as AnalyticsFilters['type'] })} sx={{ width: 150, ...fieldSx }}
        >
          {TYPE_OPTIONS.map(o => <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Маршрут" value={filters.route}
          onChange={e => onChange({ route: e.target.value as AnalyticsFilters['route'] })} sx={{ width: 190, ...fieldSx }}
        >
          <MenuItem value="">Любой</MenuItem>
          <MenuItem value="direct">Прямое перемещение</MenuItem>
          <MenuItem value="via">Через склад (РЦ)</MenuItem>
        </TextField>
        {/* признак обязательной маркировки есть только у возвратов */}
        <TextField
          select size="small" label="Маркировка (возвраты)" value={filters.marking}
          onChange={e => onChange({ marking: e.target.value as AnalyticsFilters['marking'] })} sx={{ width: 200, ...fieldSx }}
        >
          <MenuItem value="">Любая</MenuItem>
          <MenuItem value="marked">С маркировкой (КМ)</MenuItem>
          <MenuItem value="unmarked">Без маркировки</MenuItem>
        </TextField>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Autocomplete<PharmacyOption, true>
          multiple size="small" limitTags={1} disableCloseOnSelect
          options={meta.pharmacies} value={selected}
          onChange={(_, v) => onChange({ pharmacy: v.map(o => o.guid) })}
          getOptionLabel={o => o.name}
          isOptionEqualToValue={(a, b) => a.guid === b.guid}
          groupBy={o => (o.kind === 'warehouse' ? 'Склады и офисы' : 'Аптеки')}
          noOptionsText="Ничего не найдено"
          renderOption={(props, o) => (
            <li {...props} key={o.guid}>
              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 1 }}>
                <span>{o.name}</span>
                {o.region && <Typography component="span" variant="caption" color="text.secondary">{o.region}</Typography>}
              </Box>
            </li>
          )}
          renderInput={params => <TextField {...params} label="Участник (откуда или куда)" sx={fieldSx} />}
          sx={{ flex: '1 1 300px', minWidth: 260, maxWidth: 520 }}
        />
        {meta.regions.length > 1 && (
          <Autocomplete<string, true>
            multiple size="small" limitTags={2} disableCloseOnSelect
            options={meta.regions} value={filters.region}
            onChange={(_, v) => onChange({ region: v })}
            noOptionsText="Ничего не найдено"
            renderInput={params => <TextField {...params} label="Регион" sx={fieldSx} />}
            sx={{ flex: '1 1 220px', minWidth: 200, maxWidth: 400 }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {hasAnalyticsFilters(filters) && (
          <Button size="small" color="inherit" onClick={() => onChange(DEFAULT_ANALYTICS_FILTERS)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Сбросить
          </Button>
        )}
      </Box>

      {!period.error && prev && (
        <Typography sx={{ fontSize: '0.74rem', color: '#64748b' }}>
          Период: {fmtDay(period.from)} - {fmtDay(period.to)} ({period.days} {plural(period.days, 'день', 'дня', 'дней')}) ·
          сравнение с {fmtDay(prev.from)} - {fmtDay(prev.to)}. Показатели строятся по документам, созданным в периоде.
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(AnalyticsToolbar);
